import { confColorVar, confPct } from "@/lib/confidence";
import { cn } from "@/lib/utils";

/**
 * Confidence chip — extraction confidence as a %, tiered by the single helper
 * (>=.85 ok / >=.70 warn / <.70 bad). Returns null when there is no score.
 * Tint = color-mix 14% of the tier colour (vs status-badge 16%).
 */
export function ConfidenceChip({
  value,
  showDot = true,
  className,
}: {
  value: number | null | undefined;
  showDot?: boolean;
  className?: string;
}) {
  if (value == null || Number.isNaN(value)) return null;
  const color = confColorVar(value);
  return (
    <span
      className={cn("conf-chip", className)}
      style={{
        color,
        background: `color-mix(in oklab, ${color} 14%, transparent)`,
      }}
      title={`Extraction confidence ${confPct(value)}`}
    >
      {showDot && <span className="conf-dot" style={{ background: color }} />}
      {confPct(value)}
    </span>
  );
}

/**
 * Confidence ring — aggregate confidence for a whole rider/extraction.
 * SVG: viewBox 0 0 46 46, r=20, stroke 4, C=2πr=125.6,
 * dashoffset = C·(1−value), arc rotated −90° to start at 12 o'clock.
 * One ring per view; never per-field.
 */
export function ConfidenceRing({
  value,
  size = 46,
  className,
}: {
  value: number | null | undefined;
  size?: number;
  className?: string;
}) {
  const C = 125.6;
  const v = value == null || Number.isNaN(value) ? 0 : value;
  const offset = +(C * (1 - v)).toFixed(1);
  const color = confColorVar(value);
  return (
    <div
      className={cn("conf-ring", className)}
      style={{ width: size, height: size }}
      title={`Extraction confidence ${confPct(value)}`}
    >
      <svg viewBox="0 0 46 46">
        <circle cx="23" cy="23" r="20" fill="none" stroke="var(--tr-border)" strokeWidth="4" />
        <circle
          cx="23"
          cy="23"
          r="20"
          fill="none"
          stroke={color}
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray="125.6"
          strokeDashoffset={value == null ? C : offset}
          transform="rotate(-90 23 23)"
        />
      </svg>
      <span className="conf-ring__pct">
        {value == null || Number.isNaN(value) ? "—" : Math.round(value * 100)}
      </span>
    </div>
  );
}
